import React, { useState } from "react";
import Context from "../../Context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header, Loading } from "../../components/modules";
import { FiDownload } from "react-icons/fi";

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: "id", label: "รหัสสลาก" },
  { key: "fileName", label: "ชื่อไฟล์" },
  { key: "book", label: "เลขเล่ม" },
];

// ── FieldInput ────────────────────────────────────────────────────────────────

const FieldInput = ({
  label,
  value,
  onChange,
  maxLength,
  placeholder,
  wide,
}) => (
  <div className="flex flex-col gap-1">
    <p className="text-xs font-semibold text-gray-500">{label}</p>
    <input
      className={`border-2 border-gray-300 focus:border-blue outline-none rounded-lg py-1 px-2 text-center font-mono text-sm ${
        wide ? "w-80" : "w-20"
      }`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
      placeholder={placeholder}
    />
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const ImageDownloader = () => {
  const { callApi, configs } = Context();

  // Search
  const [tab, setTab] = useState("id");
  const [year, setYear] = useState("");
  const [round, setRound] = useState("");
  const [set, setSet] = useState("");
  const [fileName, setFileName] = useState("");
  const [book, setBook] = useState("");

  // Rename override (empty = keep original)
  const [YY, setYY] = useState("");
  const [RR, setRR] = useState("");
  const [SS, setSS] = useState("");

  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [failedCount, setFailedCount] = useState(0);

  // ── Search ─────────────────────────────────────────────────────────────────

  const getLotteries = async () => {
    const queries = [];
    if (tab === "id") {
      if (year) queries.push(["year", "==", year]);
      if (round) queries.push(["roundNo", "==", round]);
      if (set) queries.push(["setNo", "==", set]);
    } else if (tab === "fileName") {
      if (fileName) queries.push(["fileNameId", "==", fileName]);
    } else if (tab === "book") {
      if (book) queries.push(["bookNo", "==", book]);
    }
    setLoading(true);
    const result = await callApi(
      "lotteries-download",
      { queries },
      { notShowLoading: true },
    );
    setLotteries(result?.data || []);
    setLoading(false);
  };

  // ── Download ───────────────────────────────────────────────────────────────

  // Returns true on success, false on failure (e.g. 404/403)
  const downloadOne = async (id, url, bookNo) => {
    try {
      let [yy, rr, ss, number] = id.split("-");
      if (YY) yy = YY;
      if (RR) rr = RR;
      if (SS) ss = SS;
      const filename = [yy, rr, ss, number, bookNo].join("-");
      
      const proxyUrl = `${configs?.api || "https://asia-southeast1-hongthong-prod.cloudfunctions.net/"}lotteries-downloadProxy?url=${encodeURIComponent(url)}`;
      const res = await fetch(proxyUrl, {
        headers: {
          accessToken: localStorage.accessToken || ""
        }
      });
      if (!res.ok) {
        console.warn(`Download skipped (${res.status}): ${url}`);
        return false;
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.download = filename + ".jpeg";
      link.href = URL.createObjectURL(blob);
      link.click();
      // Small delay to avoid overwhelming the browser download queue
      await new Promise((r) => setTimeout(r, 150));
      URL.revokeObjectURL(link.href);
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const downloadAll = async () => {
    if (!lotteries.length) return;
    setDownloading(true);
    setFailedCount(0);
    let failed = 0;
    for (const lot of lotteries) {
      const ok = await downloadOne(lot.id, lot.imagePublic, lot.bookNo);
      if (!ok) failed++;
    }
    setFailedCount(failed);
    setDownloading(false);
  };
  // ── Render ─────────────────────────────────────────────────────────────────

  const hasRename = YY || RR || SS;

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto">
      <Header
        header="Download Image"
        subHeader="ค้นหาและดาวน์โหลดรูปสลาก"
        icon={<FiDownload className="text-blue" />}
      />

      {/* ── Search Card ── */}
      <div className="mt-12 bg-white rounded-lg shadow-md border border-[#E5E7E9] p-4">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`py-1 px-4 rounded-lg text-sm font-semibold transition-colors duration-150 ${
                tab === t.key
                  ? "bg-blue text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Inputs */}
        <div className="flex items-end gap-3 flex-wrap">
          {tab === "id" && (
            <>
              <FieldInput
                label="ปี (YY)"
                value={year}
                onChange={setYear}
                maxLength={2}
                placeholder="65"
              />
              <FieldInput
                label="งวด (RR)"
                value={round}
                onChange={setRound}
                maxLength={2}
                placeholder="32"
              />
              <FieldInput
                label="ชุด (SS)"
                value={set}
                onChange={setSet}
                maxLength={2}
                placeholder="13"
              />
            </>
          )}
          {tab === "fileName" && (
            <FieldInput
              label="ชื่อไฟล์"
              value={fileName}
              onChange={setFileName}
              placeholder="เช่น id-65-32-13-xxxxxxxx-jpeg"
              wide
            />
          )}
          {tab === "book" && (
            <FieldInput
              label="เลขเล่ม"
              value={book}
              onChange={setBook}
              maxLength={4}
              placeholder="2083"
            />
          )}
          <button
            className="bg-blue text-white font-semibold py-1 px-5 rounded-lg hover:opacity-80 duration-150 h-8 flex items-center gap-2 disabled:opacity-50"
            onClick={getLotteries}
            disabled={loading}
          >
            {loading ? <Loading className="w-4 h-4" /> : "ค้นหา"}
          </button>
        </div>
      </div>

      {/* ── Results ── */}
      {lotteries.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-md border border-[#E5E7E9] overflow-hidden">
          {/* Toolbar */}
          <div className="p-3 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-semibold">ผลการค้นหา</p>
              <p className="text-xs text-gray-500 mt-0.5">
                พบ {lotteries.length} รายการ
              </p>
              {failedCount > 0 && (
                <p className="text-xs text-red-500 mt-0.5">
                  ⚠ ดาวน์โหลดไม่สำเร็จ {failedCount} รายการ (ไม่พบรูปใน storage)
                </p>
              )}
            </div>

            {/* Rename + Download */}
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold text-gray-500">
                  เปลี่ยนชื่อไฟล์
                  {hasRename && (
                    <span className="ml-1 text-blue">(เปิดอยู่)</span>
                  )}
                </p>
                <div className="flex gap-2">
                  {[
                    { label: "ปี", value: YY, setter: setYY },
                    { label: "งวด", value: RR, setter: setRR },
                    { label: "ชุด", value: SS, setter: setSS },
                  ].map(({ label, value, setter }) => (
                    <input
                      key={label}
                      className="border-2 border-gray-300 focus:border-blue outline-none rounded-lg py-1 px-2 text-center font-mono text-sm w-14"
                      placeholder={label}
                      maxLength={2}
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                    />
                  ))}
                </div>
              </div>
              <button
                className="flex items-center gap-2 bg-[#52B788] text-white font-semibold py-1 px-4 rounded-lg hover:opacity-80 duration-150 h-8 disabled:opacity-50"
                onClick={downloadAll}
                disabled={downloading}
              >
                {downloading ? <Loading className="w-4 h-4" /> : <FiDownload />}
                ดาวน์โหลด ({lotteries.length})
              </button>
            </div>
          </div>

          <Table>
            <TableHeader className="bg-[#f2f3f5] border-y border-gray-300">
              <TableRow>
                <TableHead className="text-center">#</TableHead>
                <TableHead className="text-center">รูป</TableHead>
                <TableHead className="text-center">รหัสสลาก</TableHead>
                <TableHead className="text-center">เลข</TableHead>
                <TableHead className="text-center">เล่ม</TableHead>
                <TableHead className="text-center">ชื่อไฟล์</TableHead>
                <TableHead className="text-center">
                  ชื่อที่จะดาวน์โหลด
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lotteries.map((lot, i) => {
                const [yy, rr, ss, number] = lot.id.split("-");
                const outName = [
                  YY || yy,
                  RR || rr,
                  SS || ss,
                  number,
                  lot.bookNo,
                ].join("-");
                return (
                  <TableRow key={lot.id} className="h-20">
                    <TableCell className="text-center text-gray-400 text-sm">
                      {i + 1}
                    </TableCell>
                    <TableCell className="text-center w-24">
                      {lot.imagePublic ? (
                        <div className="flex items-center justify-center">
                          <img
                            src={lot.imagePublic}
                            alt={lot.id}
                            className="h-12 w-auto mx-auto rounded object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling.style.display =
                                "block";
                            }}
                          />
                          <span
                            className="text-red-400 text-xs"
                            style={{ display: "none" }}
                          >
                            404
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">ไม่มีรูป</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-center text-sm">
                      <span className="text-gray-400 text-xs">
                        {yy}-{rr}-{ss}-
                      </span>
                      <span className="font-bold">{number}</span>
                    </TableCell>
                    <TableCell className="font-bold font-mono tracking-widest text-center">
                      {number}
                    </TableCell>
                    <TableCell className="text-center">{lot.bookNo}</TableCell>
                    <TableCell className="text-center text-xs text-gray-500">
                      {lot.fileNameId}
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={`font-mono text-sm ${
                          hasRename ? "text-blue font-bold" : "text-gray-600"
                        }`}
                      >
                        {outName}.jpeg
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && lotteries.length === 0 && (
        <div className="mt-8 text-center text-gray-400">
          ค้นหาเพื่อแสดงรายการสลาก
        </div>
      )}
    </div>
  );
};

export default ImageDownloader;
